import React, { useEffect } from "react";
import styles from "./CaseDetails.module.css";
import OverviewSection from "@_components/sections/OverviewSection/OverviewSection";
import CaseActivitySection from "@_components/sections/CaseActivitySection/CaseActivitySection";
import AppSnackbar from "@_components/AppSnackbar/AppSnackbar";
import CircularProgress from "@mui/material/CircularProgress";
import { useDispatch, useSelector } from "react-redux";
import {
  useHistory,
  useLocation,
} from "react-router-dom/cjs/react-router-dom.min";
import { caseActions } from "@_actions/case.actions";
import { sidebarActions } from "@_actions/sidebar.actions";
import { catchError } from "@_utils/helpers";
import caseStates from "@_configs/caseStates.config";
import { useSnackbar } from "@_hooks/useSnackbar";

// Static config — if caseStates ever becomes runtime-dynamic this needs to move inside the component
const caseStateKeysLower = Object.keys(caseStates).map((s) => s.toLowerCase());

function CaseDetails() {
  const sidebarReducer = useSelector((state) => state.sidebarReducer);
  const caseReducer = useSelector((state) => state.caseReducer);
  const selectedCaseData =
    caseReducer.case[sidebarReducer.selections[sidebarReducer.status]];
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const pathParts = location.pathname.split("/").filter(Boolean);
  const isDirectCaseRoute = pathParts.length === 2 && pathParts[0] === "cases";
  // True when the URL's second segment is a known case-state name
  // (e.g. /cases/active, /cases/paid) rather than a real case id.
  // Uses ?. so it's safe to evaluate on routes where pathParts[1] is undefined.
  const isCaseStateSegment = caseStateKeysLower.includes(
    pathParts[1]?.toLowerCase(),
  );
  const { snackbarProps, showSnackbarError } = useSnackbar();

  /* Initializes and validates the case details view based on the current URL path.

  Responsibilities:
  1. Ensures the sidebar status is set to "CASES" if not already valid
  2. Fetches case details when navigating to a specific case via URL

  @triggers
  When the URL path matches the pattern: /cases/:caseId
  Example: /cases/123 or /cases/ABC-456
  */
  useEffect(() => {
    if (isDirectCaseRoute) {
      if (
        !sidebarReducer.status ||
        !caseStateKeysLower.includes(sidebarReducer.status.toLowerCase())
      ) {
        dispatch(sidebarActions.updateStatus("CASES"));
      }
      if (!isCaseStateSegment) {
        dispatch(caseActions.getCaseWithChargeDetails(pathParts[1]));
      }
    }
  }, [dispatch, location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Handles the result of fetching case details and updates the UI accordingly.

  Processes the API response and either:
  - Updates sidebar selections if case was found successfully
  - Navigates to 404 page if case doesn't exist
  - Shows a snackbar error if the API returned success but the case wasn't found in the store
  - Clears loading/error messages in all cases

  @triggers
  When caseReducer.status.CASE_GET changes (API call completes)
  Must be on URL path: /cases/:caseId where :caseId is an actual case id,
  NOT a case state segment like /cases/active or /cases/paid.

  Status segments are excluded because CASE_GET is a shared status used by
  many other components (popups, activity cards, etc.) that dispatch
  caseActions.getCaseWithChargeDetails. Without !isCaseStateSegment guard, 
  an unrelated fetch completing while the user is on a status URL would incorrectly
  trigger the "Case could not be loaded" snackbar, since no case in the
  store has case_id matching a status name.
  */
  useEffect(() => {
    if (isDirectCaseRoute && !isCaseStateSegment) {
      if (caseReducer.status.CASE_GET === "success") {
        const routedCase = Object.values(caseReducer.case).find(
          (c) => c.case_id?.toLowerCase() === pathParts[1].toLowerCase(),
        );
        if (routedCase) {
          dispatch(sidebarActions.updateSelections(routedCase.id));
        }
        dispatch(caseActions.clearStateMessage("CASE_GET"));
      } else if (catchError(caseReducer.status.CASE_GET)) {
        if (caseReducer.status.CASE_GET?.includes("Resource not found")) {
          history.push("/page_not_found");
        } else {
          showSnackbarError(caseReducer.status.CASE_GET);
        }
        dispatch(caseActions.clearStateMessage("CASE_GET"));
      }
    }
  }, [dispatch, caseReducer.status.CASE_GET, showSnackbarError]); // eslint-disable-line react-hooks/exhaustive-deps

  const isFetching =
    isDirectCaseRoute &&
    caseReducer.status.CASE_GET === "request" &&
    !selectedCaseData;

  return (
    <div className={styles.root}>
      <AppSnackbar {...snackbarProps} />
      {isFetching ? (
        <div className={styles.loading}>
          <CircularProgress />
        </div>
      ) : selectedCaseData ? (
        <div className={styles.case_content}>
          <div className={styles.top_container}>
            <OverviewSection data={selectedCaseData} />
          </div>
          <div className={styles.section_container}>
            <CaseActivitySection data={selectedCaseData} />
          </div>
        </div>
      ) : (
        <div className={styles.no_data}>No Case is Selected!</div>
      )}
    </div>
  );
}

export default CaseDetails;
